<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { QInfiniteScroll } from 'quasar'
import { useFeaturedStore } from '../stores/featured'
import PreviewIncidentCard from './PreviewIncidentCard.vue'

const featuredStore = useFeaturedStore()
const { featuredIncidents, isLoading, hasMore } = storeToRefs(featuredStore)
const router = useRouter()
const infiniteScrollRef = ref<InstanceType<typeof QInfiniteScroll> | null>(null)
const firstLoadComplete = ref(false)

onMounted(async () => {
  featuredStore.reset()
  await featuredStore.fetchFeaturedIncidents()
  firstLoadComplete.value = true
})

async function onLoad(_index: number, done: (stop?: boolean) => void) {
  if (!hasMore.value) {
    done(true)
    return
  }
  await featuredStore.fetchFeaturedIncidents()
  done(!hasMore.value)
}

function loadMore() {
  infiniteScrollRef.value?.trigger()
}
</script>

<template>
  <div class="q-pa-md">
    <!-- Skeleton placeholders shown while first page is loading -->
    <div v-if="!firstLoadComplete" class="row q-col-gutter-md">
      <div v-for="n in 8" :key="n" class="col-12 col-sm-12 col-md-6 col-lg-3">
        <q-card flat bordered>
          <q-skeleton type="rect" style="aspect-ratio: 16/9; width: 100%" />
          <q-card-section>
            <q-skeleton type="text" class="text-overline" width="45%" />
            <div style="height: calc(3 * 0.875rem * 1.375); overflow: hidden" class="q-mb-sm">
              <q-skeleton type="text" class="text-subtitle2" />
              <q-skeleton type="text" class="text-subtitle2" />
              <q-skeleton type="text" class="text-subtitle2" width="75%" />
            </div>
            <div style="height: calc(2 * 0.75rem * 1.667); overflow: hidden">
              <q-skeleton type="text" class="text-caption" />
              <q-skeleton type="text" class="text-caption" width="65%" />
            </div>
            <q-skeleton type="text" class="text-caption q-mt-xs" width="50%" />
          </q-card-section>
        </q-card>
      </div>
    </div>

    <template v-else-if="firstLoadComplete">
      <!-- Empty state -->
      <div v-if="featuredIncidents.length === 0" class="text-center text-blue-grey-5 q-pa-xl">
        <q-icon name="mdi-video-off-outline" size="4rem" class="q-mb-md" />
        <div class="text-h6">No incidents posted yet</div>
        <div class="text-body2 q-mt-sm">Check back soon.</div>
      </div>

      <!--
        q-infinite-scroll only mounts after the first page is loaded so its
        onLoad fires for page 2+ only — skeletons no longer block the trigger.
      -->
      <q-infinite-scroll v-else ref="infiniteScrollRef" @load="onLoad" :offset="250">
        <div class="row q-col-gutter-md">
          <div
            v-for="incident in featuredIncidents"
            :key="incident.id"
            class="col-12 col-sm-12 col-md-6 col-lg-3"
          >
            <preview-incident-card
              :incident="incident"
              @click="router.push({ name: 'preview', params: { incidentReportId: incident.id } })"
            />
          </div>
        </div>

        <template #loading>
          <div class="row justify-center q-my-md">
            <q-spinner-dots color="primary" size="40px" />
          </div>
        </template>
      </q-infinite-scroll>

      <div
        v-if="featuredIncidents.length > 0 && hasMore && !isLoading"
        class="row justify-center q-my-md"
      >
        <q-btn flat color="primary" label="Load more" icon="mdi-chevron-down" @click="loadMore" />
      </div>
    </template>
  </div>
</template>
